/**
 * Pure helpers for the bulk course-attendance sign-in sheet.
 *
 * The sheet is a CSV (so Excel reads/writes it natively with zero extra deps).
 * Email (`loginName`) is the unique identifier, matched case-insensitively.
 */

const BOM = '﻿';

export interface ValidationSheetRow {
  loginName?: string;
  firstName?: string;
  lastName?: string;
  timeIn?: string;
  timeOut?: string;
  [key: string]: any;
}

/** One row from the sheet, as the admin typed it. */
export interface AttendanceSheetEntry {
  /** Lowercased/trimmed, matching the entries in `allEmails`. */
  email: string;
  firstName: string;
  lastName: string;
  attended: boolean;
  /**
   * Sign-in times, kept verbatim. They are never parsed or checked — the sheet
   * is the source of truth and the app only carries them through.
   */
  timeIn: string;
  timeOut: string;
}

export interface ParsedAttendanceSheet {
  attendedEmails: Set<string>;
  allEmails: Set<string>;
  /**
   * Every row that carries an email, in sheet order, with the names and times
   * alongside it. Walk-ins added by hand are read from here so they can be
   * shown by name before they are matched to an account.
   */
  entries: AttendanceSheetEntry[];
  invalidFormat: boolean;
  /** Course ID read from the sheet, if the sheet carries one (empty otherwise). */
  sheetCourseId: string;
  /** Course Name read from the sheet, if present (empty otherwise). */
  sheetCourseName: string;
}

/** Wrap a single cell in quotes, escaping any embedded quotes per RFC-4180. */
function quoteCell(value: any): string {
  const str = value === null || value === undefined ? '' : String(value);
  return `"${str.replace(/"/g, '""')}"`;
}

/**
 * Build the downloadable sign-in sheet CSV.
 * Header: Course Name, Course ID, First Name, Last Name, Email,
 * "Attended (mark X)", Time In, Time Out.
 * The Attended column is left blank for the user to mark; the time columns are
 * pre-filled with whatever has already been recorded, so a re-download of a
 * sheet carries the previous entries.
 */
export function buildValidationSheetCsv(
  courseName: string,
  courseItemId: string | number,
  rows: ValidationSheetRow[] = [],
): string {
  const header = [
    'Course Name',
    'Course ID',
    'First Name',
    'Last Name',
    'Email',
    'Attended (mark X)',
    'Time In',
    'Time Out',
  ];

  const lines = [ header ];

  (rows || []).forEach((row) => {
    lines.push([
      courseName ?? '',
      String(courseItemId ?? ''),
      row?.firstName ?? '',
      row?.lastName ?? '',
      row?.loginName ?? '',
      '',
      row?.timeIn ?? '',
      row?.timeOut ?? '',
    ]);
  });

  const csv = lines
    .map((cells) => cells.map(quoteCell).join(','))
    .join('\r\n');

  return BOM + csv;
}

/** Build a filesystem-safe filename: validation_<slug>_<id>.csv */
export function validationSheetFilename(courseName: string,
  courseItemId: string | number): string {
  const slug = String(courseName ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

  const id = String(courseItemId ?? '').replace(/[^a-z0-9]+/gi, '');

  const safeSlug = slug || 'course';

  return `validation_${safeSlug}_${id}.csv`;
}

/**
 * Minimal RFC-4180 CSV parser.
 * Handles: quoted fields, escaped quotes (""), commas inside quotes,
 * `\r\n` and `\n` line endings, and a leading BOM. Fully blank rows are dropped.
 */
export function parseCsv(text: string): string[][] {
  if (!text) return [];

  // Strip a leading BOM if present.
  let input = text;
  if (input.charCodeAt(0) === 0xfeff) {
    input = input.slice(1);
  }

  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  let i = 0;

  const pushField = () => {
    row.push(field);
    field = '';
  };

  const pushRow = () => {
    pushField();
    // Drop fully blank rows (every cell empty after trimming).
    const isBlank = row.every((cell) => cell.trim() === '');
    if (!isBlank) rows.push(row);
    row = [];
  };

  while (i < input.length) {
    const char = input[i];

    if (inQuotes) {
      if (char === '"') {
        if (input[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i += 1;
        continue;
      }
      field += char;
      i += 1;
      continue;
    }

    if (char === '"') {
      inQuotes = true;
      i += 1;
      continue;
    }

    if (char === ',') {
      pushField();
      i += 1;
      continue;
    }

    if (char === '\r') {
      // Handle CRLF and lone CR.
      if (input[i + 1] === '\n') i += 1;
      pushRow();
      i += 1;
      continue;
    }

    if (char === '\n') {
      pushRow();
      i += 1;
      continue;
    }

    field += char;
    i += 1;
  }

  // Flush the final field/row if there is any pending content.
  if (field !== '' || row.length > 0) {
    pushRow();
  }

  return rows;
}

/**
 * Parse an uploaded attendance sheet.
 * Locates the Email column (header includes "email") and the Attended column
 * (header includes "attend"). If either is missing, `invalidFormat` is true.
 * Emails are lowercased/trimmed. A cell counts as attended if it contains an
 * `x` (case-insensitive) or equals yes/y/true/1.
 * Time In / Time Out are optional and copied through verbatim.
 */
export function parseAttendanceSheet(text: string): ParsedAttendanceSheet {
  const attendedEmails = new Set<string>();
  const allEmails = new Set<string>();
  const entries: AttendanceSheetEntry[] = [];

  const rows = parseCsv(text);
  if (rows.length === 0) {
    return {
      attendedEmails, allEmails, entries, invalidFormat: true, sheetCourseId: '', sheetCourseName: '',
    };
  }

  const header = rows[0].map((cell) => cell.trim().toLowerCase());
  const emailIndex = header.findIndex((cell) => cell.includes('email'));
  const attendIndex = header.findIndex((cell) => cell.includes('attend'));
  // "Course ID" and "Course Name" both contain "course", so disambiguate on the
  // second word. These columns are optional — absent on hand-made sheets.
  const courseIdIndex = header.findIndex((cell) => cell.includes('course') && cell.includes('id'));
  const courseNameIndex = header.findIndex((cell) => cell.includes('course') && cell.includes('name'));
  // Names are optional — they only decorate walk-in rows before they are matched.
  const firstNameIndex = header.findIndex((cell) => cell.includes('first') && cell.includes('name'));
  const lastNameIndex = header.findIndex((cell) => cell.includes('last') && cell.includes('name'));
  // Times are optional too — a sheet made before these columns existed, or by
  // hand, simply carries no times.
  const timeInIndex = header.findIndex((cell) => cell.includes('time') && cell.includes('in'));
  const timeOutIndex = header.findIndex((cell) => cell.includes('time') && cell.includes('out'));

  if (emailIndex === -1 || attendIndex === -1) {
    return {
      attendedEmails, allEmails, entries, invalidFormat: true, sheetCourseId: '', sheetCourseName: '',
    };
  }

  let sheetCourseId = '';
  let sheetCourseName = '';

  for (let r = 1; r < rows.length; r += 1) {
    const cells = rows[r];

    // The course columns repeat on every data row; capture the first non-empty.
    if (!sheetCourseId && courseIdIndex !== -1) {
      sheetCourseId = String(cells[courseIdIndex] ?? '').trim();
    }
    if (!sheetCourseName && courseNameIndex !== -1) {
      sheetCourseName = String(cells[courseNameIndex] ?? '').trim();
    }

    const email = String(cells[emailIndex] ?? '').trim().toLowerCase();
    if (!email) continue;

    const attended = isAttendedValue(cells[attendIndex]);

    // The same email on two rows is one person, not two: the first row wins,
    // and a later row can only add the attendance mark.
    if (!allEmails.has(email)) {
      entries.push({
        email,
        firstName: firstNameIndex === -1 ? '' : String(cells[firstNameIndex] ?? '').trim(),
        lastName: lastNameIndex === -1 ? '' : String(cells[lastNameIndex] ?? '').trim(),
        attended,
        timeIn: timeInIndex === -1 ? '' : String(cells[timeInIndex] ?? '').trim(),
        timeOut: timeOutIndex === -1 ? '' : String(cells[timeOutIndex] ?? '').trim(),
      });
    } else if (attended) {
      const entry = entries.find((row) => row.email === email);
      if (entry) entry.attended = true;
    }

    allEmails.add(email);
    if (attended) attendedEmails.add(email);
  }

  return {
    attendedEmails, allEmails, entries, invalidFormat: false, sheetCourseId, sheetCourseName,
  };
}

/** A cell counts as attended if it contains an `x` or equals yes/y/true/1. */
function isAttendedValue(value: any): boolean {
  const cell = String(value ?? '').trim().toLowerCase();
  if (!cell) return false;
  if (cell.includes('x')) return true;
  return cell === 'yes' || cell === 'y' || cell === 'true' || cell === '1';
}
